import {Component, DoCheck, Inject, OnInit} from '@angular/core';
import {EMPTY, forkJoin, Observable} from 'rxjs';
import {expand, reduce} from 'rxjs/operators';
import {AppConstants} from '../../shared/constants/app-constants';
import {HomeAction} from './home.action';
import {DataManagerService} from '../../data/services/DataManager/data-manager.service';
import {DatePipe} from '@angular/common';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {BarSeries} from '../../ui/bar-chart/payarc-bar-chart.component';
import {DonutSegment} from '../../ui/donut/payarc-donut.component';
import {SegOption} from '../../ui/segmented/payarc-segmented.component';
import {PAYARC_DATE_FORMATS} from '../../shared/material-date-formats';
import {PermissionConstants} from '../../shared/constants/permission-constants';

/** Visa's published VAMP "excessive" dispute-ratio threshold; not merchant-specific data. */
const VAMP_RATIO_LIMIT = 0.9;

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false,
    providers: [{
      provide: MAT_DATE_FORMATS,
      useValue: { ...PAYARC_DATE_FORMATS, display: { ...PAYARC_DATE_FORMATS.display, dateInput: { year: 'numeric', month: 'short', day: '2-digit' } } }
    }]
})
export class HomeComponent extends HomeAction implements OnInit, DoCheck {

  readonly vampRatioLimit = VAMP_RATIO_LIMIT;

  dateSet = AppConstants.homeDayDifference;
  dateUnit = AppConstants.homeDayUnitDifference;
  rangeOptions: SegOption[] = this.dateSet.map(d => ({label: d.text, value: d.text}));
  today = new Date();
  fromDate: Date;
  toDate: Date;
  criteria: string;
  daySetActive: string;
  daySetActiveOffset: number;
  userInput: boolean;
  currentResolution: number;

  // Key metrics (GET /reports)
  totalSales: number;
  totalRefunds: number;
  netSales: number;
  avg_revenue: number;
  total_transactions: number;
  refundPercentage: number;
  t_disputes: number;
  p_disputes: number;
  tv_disputes: number;
  pv_disputes: number;

  // Sales over time (GET /reports · v_net_gross)
  salesBarCategories: string[] = [];
  salesBarSeries: BarSeries[] = [
    {label: 'Gross sales', color: 'var(--pa-mint)', values: []},
    {label: 'Refunds', color: 'var(--pa-navy)', values: []},
    {label: 'Dispute', color: 'var(--pa-blue-light)', values: []},
  ];

  // Card-type mix (GET /reports · s_card_type)
  cardTypeSegments: DonutSegment[] = [];

  pieChartColorsLables = {
    'R': {color: '#CBCBCB', label: 'Discover'},
    'V': {color: '#4AD291', label: 'Visa'},
    'X': {color: '#121744', label: 'AMEX'},
    'M': {color: '#419DF1', label: 'Master Card'},
    'J': {color: '#92CCDC', label: 'JCB'},
    1: {color: '#419DF1', label: 'Master'},
    'U': {color: '#0eaeee', label: 'Unknown'},
    'I': {color: '#3168A9', label: 'Diners Club'},
    'E': {color: '#3166AC', label: 'EBT Card'},
    'D': {color: '#674EA7', label: 'Debit Card'},
    'A': {color: '#7F6006', label: 'ATM Card'},
    'G': {color: '#6AA84F', label: 'Private Label Gift Card'},
  };

  /** Same brand->logo mapping payment-list.component.ts uses for the real transactions list. */
  cardImgObj = {
    'R': '../../../assets/images/disc.png',
    'V': '../../../assets/images/visa.png',
    'X': '../../../assets/images/amex.png',
    'M': '../../../assets/images/master.png',
    'J': '../../../assets/images/jcb.png',
  };
  defaultCardImg = '../../../assets/images/card-default.png';

  // Needs attention (derived from already-fetched data, no extra call)
  notRespondedDisputes = 0;

  // Dispute resolution + trend (GET /dispute-chart)
  disputeChartLoading = true;
  disputeChartError = false;
  disputeDonutSegments: DonutSegment[] = [];
  disputeWinRate: number = null;
  disputeTotalCount = 0;
  disputeTrendCategories: string[] = [];
  disputeTrendSeries: BarSeries[] = [{label: 'Disputes', color: 'var(--pa-coral)', values: []}];

  private readonly disputeColorMap: Record<string, string> = {
    'Currently resolved in Merchant\'s favor': 'var(--pa-mint)',
    'Currently resolved in Bank\'s favor': 'var(--pa-navy)',
    'Pending': 'var(--pa-blue-light)',
    'RDR': 'var(--pa-blue-med)',
    'Responded': 'var(--pa-purple)',
    'Not Responded': 'var(--pa-coral)',
  };

  // Deposits (GET /deposit/summary)
  depositsLoading = true;
  depositsError = false;
  depositsPeriodTotal: number = null;

  // Subscriptions + Est. MRR (GET /subscriptions)
  subscriptionsLoading = true;
  subscriptionsError = false;
  subActiveCount = 0;
  subTrialCount = 0;
  subCanceledCount = 0;
  subEstMrr = 0;

  // VAMP ratio (GET /vamp/summary — known to 503 in some envs; degrades gracefully)
  vampLoading = true;
  vampError = false;
  vampRatio: number = null;

  // Top ACH reject reasons (GET /ach-rejects, client-aggregated); only with the ach-rejects permission
  showAchRejects = false;
  achLoading = true;
  achError = false;
  achReasons: { label: string; amount: number; count: number }[] = [];

  // Recent activity (GET /events)
  activityLoading = true;
  activityError = false;
  recentActivity: any[] = [];

  // Recent transactions (GET /charges)
  txnsLoading = true;
  txnsError = false;
  recentTransactions: any[] = [];

  get userAccount(): any {
    return JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_ACCOUNT_ID));
  }

  constructor(dataManager: DataManagerService, private _datePipe: DatePipe, snackBar: MatSnackBar,
              @Inject('LOCALSTORAGE') private localStorage: any) {
    super();
    this.snackBar = snackBar;
    this.dataManager = dataManager;
    this.daySetActiveOffset = 6;
    this.daySetActive = '1w';
    this.criteria = 'day';
    this.userInput = true;
    this.fromDate = this.addDays(new Date(), this.daySetActiveOffset);
    this._getAllStatesUrl = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_REPORTS;
    this.toDate = new Date();
    this.currentResolution = window.screen.width;
    this.loadFlag = true;
    this.setUrls();
  }

  ngOnInit() {
    // Not date-range scoped — fetched once, independent of the KPI/chart widgets.
    this.fetchSubscriptions();
    this.showAchRejects = this.permissionCheck(PermissionConstants.PERMISSION_ACH_REJECTS);
    if (this.showAchRejects) {
      this.fetchAchRejects();
    }
    this.fetchRecentActivity();
    this.fetchRecentTransactions();
  }

  ngDoCheck() {
    if (window.screen.width !== this.currentResolution) {
      this.currentResolution = window.screen.width;
    }
  }

  onRangeChange(value: string) {
    const match = this.dateSet.find(d => d.text === value);
    if (match) {
      this.dateSetFun(match);
    }
  }

  setUrls() {
    this.loadFlag = true;
    const from = this._datePipe.transform(this.fromDate, 'yyyy-MM-dd');
    const to = this._datePipe.transform(this.toDate, 'yyyy-MM-dd');
    this.getAllStates(this._getAllStatesUrl + '?from=' + from + '&to=' + to + '&criteria=' + this.criteria);
    this.fetchDisputeChart(from, to);
    this.fetchDeposits(from, to);
    this.fetchVamp(from, to);
  }

  dateSetFun(arg) {
    this.loadFlag = true;
    this.daySetActive = arg.text;
    this.dateUnit = arg.dateUnit;
    this.daySetActiveOffset = arg.offset;
    if (arg.is_today_start) {
      this.toDate = new Date();
      this.fromDate = this.addDaysTD(arg.text);
      this.userInput = false;
    } else if (arg.text === 'All') {
      if (this.userAccount[0].created_at) {
        this.toDate = new Date();
        this.fromDate = new Date('2019-01-01');
        const diffDays = this.dateDifference(this.fromDate, this.toDate);
        diffDays <= 28 ? this.criteria = 'day' : diffDays <= 150 ? this.criteria = 'week' :
          this.criteria = 'month';
      } else {
        this.showSnackBar('User account info issue', 'Ok', 3000);
      }
    } else if (arg.text === '1w') {
      this.fromDate = new Date(this.toDate.getFullYear(), this.toDate.getMonth(), this.toDate.getDate() - 6);
    } else if (arg.text === '4w') {
      this.fromDate = new Date(this.toDate.getFullYear(), this.toDate.getMonth(), this.toDate.getDate() - 27);
    } else if (arg.text === '1y') {
      this.fromDate = new Date(this.toDate.getFullYear() - 0.5, this.toDate.getMonth(), this.toDate.getDate() - 1);
    }
    if (arg.dateUnit.length > 0) {
      for (const criteria of arg.dateUnit) {
        if (criteria.is_default) {
          this.criteria = criteria.criteria;
        }
      }
    }
    this.setUrls();
  }

  setDateFrom() {
    if (this.toDate < this.fromDate) {
      this.toDate = new Date(this.fromDate);
    }
    const diffDays = this.dateDifference(this.fromDate, this.toDate);
    diffDays <= 28 ? this.criteria = 'day' : diffDays <= 150 ? this.criteria = 'week' :
      this.criteria = 'month';
    this.dateUnit = [];
    this.daySetActive = '1';
    this.setUrls();
  }

  /**
   * func to set date based on option selected
   * @param date
   * @param days
   * @returns {Date}
   */
  addDays(date: Date, days: number): Date {
    const d = date;
    d.setDate(date.getDate() - days);
    return d;
  }

  addDaysTD(key) {
    const date = new Date();
    switch (key) {
      case 'Mtd':
        date.setDate(1);
        break;
      case 'Qtd':
        if (date.getMonth() <= 2) {
          date.setMonth(1);
        } else if (date.getMonth() > 2 && date.getMonth() <= 5) {
          date.setMonth(3);
        } else if (date.getMonth() > 5 && date.getMonth() <= 8) {
          date.setMonth(6);
        } else if (date.getMonth() > 8 && date.getMonth() <= 11) {
          date.setMonth(9);
        }
        date.setDate(1);
        break;
      case 'Ytd':
        date.setDate(1);
        date.setMonth(0);
        break;
    }
    return date;
  }

  protected passAllStatesResponse(res) {
    this.loadFlag = false;
    this.netSales = res.n_sales;
    this.avg_revenue = res.avg_tranx;
    this.totalRefunds = res.t_refunds;
    this.total_transactions = res.t_tranx;
    this.refundPercentage = res.p_refunds;
    this.totalSales = res.t_sales;
    this.t_disputes = res.t_disputes;
    this.p_disputes = res.p_disputes;
    this.tv_disputes = res.tv_disputes;
    this.pv_disputes = res.pv_disputes;
    this.setSalesBarSeries(res.v_net_gross ? res.v_net_gross : []);
    this.setCardTypeSegments(res.s_card_type ? res.s_card_type : []);
  }

  protected passError(error) {
    this.loadFlag = false;
  }

  private setSalesBarSeries(rows: any[]) {
    const categories: string[] = [];
    const gross: number[] = [];
    const refunds: number[] = [];
    const disputes: number[] = [];
    rows.forEach(row => {
      categories.push(this.formatBarLabel(row.label));
      gross.push((row.gross_sales || 0) / 100);
      refunds.push((row.refund_amount || 0) / 100);
      disputes.push((row.dispute_amount || 0) / 100);
    });
    this.salesBarCategories = categories;
    this.salesBarSeries = [
      {label: 'Gross sales', color: 'var(--pa-mint)', values: gross},
      {label: 'Refunds', color: 'var(--pa-navy)', values: refunds},
      {label: 'Dispute', color: 'var(--pa-blue-light)', values: disputes},
    ];
  }

  private formatBarLabel(label): string {
    if (typeof label !== 'number') {
      return label;
    }
    const last = label.toString().split('').pop();
    if (label > 3 && label < 21) {
      return label + 'th Week';
    }
    if (last === '1') {
      return label + 'st Week';
    }
    if (last === '2') {
      return label + 'nd Week';
    }
    if (last === '3') {
      return label + 'rd Week';
    }
    return label + 'th Week';
  }

  private setCardTypeSegments(rows: any[]) {
    this.cardTypeSegments = rows
      .filter(row => !this.isNull(row.card_type) && this.pieChartColorsLables[row.card_type])
      .map(row => ({
        label: this.pieChartColorsLables[row.card_type].label,
        value: Number(row.card_tranx_percentage),
        color: this.pieChartColorsLables[row.card_type].color,
      }));
  }

  private fetchDisputeChart(from: string, to: string) {
    this.disputeChartLoading = true;
    this.disputeChartError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_DISPUTE_CHART
      + '?report_date[gte]=' + from + '&report_date[lte]=' + to;
    this.dataManager.getRequest(url, 0).subscribe(
      (res) => this.passDisputeChartResponse(res),
      () => {
        this.disputeChartLoading = false;
        this.disputeChartError = true;
        this.notRespondedDisputes = 0;
      }
    );
  }

  private passDisputeChartResponse(res: any) {
    this.disputeChartLoading = false;
    const piData: any[] = res?.pi_chart_data ?? [];
    const won = piData.find(r => r.data_type === 'Currently resolved in Merchant\'s favor');
    const resolvedCount = piData
      .filter(r => r.data_type === 'Currently resolved in Merchant\'s favor' || r.data_type === 'Currently resolved in Bank\'s favor')
      .reduce((sum, r) => sum + Number(r.dispute_count || 0), 0);
    this.disputeTotalCount = piData.reduce((sum, r) => sum + Number(r.dispute_count || 0), 0);
    this.disputeWinRate = resolvedCount > 0 ? Math.round((Number(won?.dispute_count || 0) / resolvedCount) * 100) : null;
    this.disputeDonutSegments = piData
      .filter(r => Number(r.dispute_count) > 0)
      .map(r => ({
        label: r.data_type,
        value: Number(r.dispute_count),
        color: this.disputeColorMap[r.data_type] || 'var(--pa-blue)',
      }));

    const notResponded = piData.find(r => r.data_type === 'Not Responded');
    this.notRespondedDisputes = Number(notResponded?.dispute_count || 0);

    const trend: any[] = res?.dispute_count_chart_data ?? [];
    this.disputeTrendCategories = trend.map(r => r.label);
    this.disputeTrendSeries = [{
      label: 'Disputes',
      color: 'var(--pa-coral)',
      values: trend.map(r => Number(r.dispute_count || 0)),
    }];
  }

  private fetchDeposits(from: string, to: string) {
    this.depositsLoading = true;
    this.depositsError = false;
    const url = AppConstants.BASE_URL + '/deposit/summary?from_date=' + from + '&to_date=' + to;
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.depositsLoading = false;
        this.depositsPeriodTotal = res?.data?.rows_data?.grand_total?.total_amount ?? 0;
      },
      () => {
        this.depositsLoading = false;
        this.depositsError = true;
      }
    );
  }

  private fetchVamp(from: string, to: string) {
    this.vampLoading = true;
    this.vampError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_VAMP + '/summary?from=' + from + '&to=' + to;
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.vampLoading = false;
        const row = res?.data?.[0];
        this.vampRatio = row ? Number(row.vamp_ratio_pct) : null;
      },
      () => {
        this.vampLoading = false;
        this.vampError = true;
      }
    );
  }

  private fetchSubscriptions() {
    this.subscriptionsLoading = true;
    this.subscriptionsError = false;
    const base = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_SUBSCRIPTIONS;
    const countUrl = (status: string) => base + '?limit=1&page=1&status[in]=' + status;

    forkJoin({
      active: this.dataManager.getRequest(countUrl('active'), 0),
      trial: this.dataManager.getRequest(countUrl('trial'), 0),
      canceled: this.dataManager.getRequest(countUrl('canceled'), 0),
      activeMrr: this.fetchActiveSubscriptionAmounts(),
    }).subscribe(
      (res: any) => {
        this.subscriptionsLoading = false;
        this.subActiveCount = res.active?.meta?.pagination?.total ?? 0;
        this.subTrialCount = res.trial?.meta?.pagination?.total ?? 0;
        this.subCanceledCount = res.canceled?.meta?.pagination?.total ?? 0;
        this.subEstMrr = res.activeMrr;
      },
      () => {
        this.subscriptionsLoading = false;
        this.subscriptionsError = true;
      }
    );
  }

  /**
   * Sums plan.data.amount across every page of active subscriptions (not just the first),
   * following the same links.next-truthiness convention BaseListComponent uses elsewhere —
   * an account with more active subscriptions than one page must not understate Est. MRR.
   */
  private fetchActiveSubscriptionAmounts(limit = 200): Observable<number> {
    const base = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_SUBSCRIPTIONS;
    const pageUrl = (page: number) => base + '?limit=' + limit + '&page=' + page + '&status[in]=active&include=plan';
    return this.dataManager.getRequest(pageUrl(1), 0).pipe(
      expand((res: any, index: number) =>
        res?.meta?.pagination?.links?.next ? this.dataManager.getRequest(pageUrl(index + 2), 0) : EMPTY
      ),
      reduce((sum: number, res: any) =>
        sum + (res?.data ?? []).reduce((s: number, row: any) => s + (row?.plan?.data?.amount || 0), 0), 0)
    );
  }

  private fetchAchRejects() {
    this.achLoading = true;
    this.achError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_ACH_REJECTS + '?limit=100&page=1';
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.achLoading = false;
        const rows: any[] = res?.data ?? [];
        const byReason = new Map<string, { amount: number; count: number }>();
        rows.forEach(row => {
          const label = row.reason_description || row.reason_code || 'Unknown';
          const entry = byReason.get(label) || {amount: 0, count: 0};
          entry.amount += Number(row.amount || 0);
          entry.count += 1;
          byReason.set(label, entry);
        });
        this.achReasons = Array.from(byReason.entries())
          .map(([label, v]) => ({label, amount: v.amount, count: v.count}))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 5);
      },
      () => {
        this.achLoading = false;
        this.achError = true;
      }
    );
  }

  private fetchRecentActivity() {
    this.activityLoading = true;
    this.activityError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_EVENT + '?limit=8&page=1';
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.activityLoading = false;
        this.recentActivity = res?.data ?? [];
      },
      () => {
        this.activityLoading = false;
        this.activityError = true;
      }
    );
  }

  private fetchRecentTransactions() {
    this.txnsLoading = true;
    this.txnsError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_CHARGE + '?limit=8&page=1';
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.txnsLoading = false;
        this.recentTransactions = res?.data ?? [];
      },
      () => {
        this.txnsLoading = false;
        this.txnsError = true;
      }
    );
  }
}
